import type { Id, TitleSummary } from '@pk/shared';
import { mulberry32 } from './color';

/* Land geometry for the world map: one Voronoi cell per holding, clipped to an organic coastline.
   Dependency-free (half-plane clipping) and O(n^2); fine for a few hundred holdings. */

export interface Pt { x: number; y: number }

export interface LandCell {
  /** Index into the input holdings array. */
  idx: number;
  id: Id;
  x: number;
  y: number;
  /** Ring of points (edge k runs pts[k] -> pts[k+1]). */
  pts: Pt[];
  /** Per edge: index of the neighbouring holding across it, or -1 for coast. */
  nb: number[];
  area: number;
  /** Closed SVG path of the ring. */
  path: string;
}

export interface Glyph { x: number; y: number; k: string; s: number; f: boolean }

export interface Bounds { x0: number; y0: number; x1: number; y1: number }

export interface HoldingPoint { id: Id; x: number; y: number }

const r1 = (n: number) => Math.round(n * 10) / 10;

/** Walk a title's de-jure chain up to the given rank. */
export function ancestorOfRank(t: TitleSummary, rank: string, byId: Map<Id, TitleSummary>): TitleSummary | null {
  let cur: TitleSummary | undefined = t;
  let guard = 0;
  while (cur && cur.rank !== rank && cur.deJureParentId && guard++ < 6) cur = byId.get(cur.deJureParentId);
  return cur && cur.rank === rank ? cur : null;
}

/* ---------- smooth deterministic noise ---------- */
function hash2(ix: number, iy: number, seed: number): number {
  let h = Math.imul(ix, 374761393) ^ Math.imul(iy, 668265263) ^ Math.imul(seed, 2147483647);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}
function vnoise(x: number, y: number, seed: number): number {
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  const fx = x - ix;
  const fy = y - iy;
  const sx = fx * fx * (3 - 2 * fx);
  const sy = fy * fy * (3 - 2 * fy);
  const a = hash2(ix, iy, seed);
  const b = hash2(ix + 1, iy, seed);
  const c = hash2(ix, iy + 1, seed);
  const d = hash2(ix + 1, iy + 1, seed);
  return (a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy) * 2 - 1;
}
/** Continuous displacement field: neighbouring cells see the same offset at a shared point, so they stay seamless. */
function displace(p: Pt, amp: number): Pt {
  return {
    x: p.x + amp * (vnoise(p.x / 24, p.y / 24, 11) + 0.45 * vnoise(p.x / 9, p.y / 9, 23)),
    y: p.y + amp * (vnoise(p.x / 24, p.y / 24, 37) + 0.45 * vnoise(p.x / 9, p.y / 9, 51)),
  };
}

interface V { x: number; y: number; tag: number }

/** Keep the half-plane (p - mid) . n <= 0 ; new edges created on the cut line get `tag`. */
function clipHalf(poly: V[], mid: Pt, n: Pt, tag: number): V[] {
  const out: V[] = [];
  const m = poly.length;
  const side = (p: Pt) => (p.x - mid.x) * n.x + (p.y - mid.y) * n.y;
  for (let i = 0; i < m; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % m];
    const sa = side(a);
    const sb = side(b);
    const ain = sa <= 0;
    const bin = sb <= 0;
    if (ain && bin) {
      out.push(a);
    } else if (ain && !bin) {
      // leaving: keep a, then the crossing; the edge from the crossing runs along the cut
      const t = sa / (sa - sb);
      out.push(a, { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, tag });
    } else if (!ain && bin) {
      // entering: the crossing continues along the original edge a->b
      const t = sa / (sa - sb);
      out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, tag: a.tag });
    }
  }
  return out;
}

function polyArea(p: Pt[]): number {
  let s = 0;
  for (let i = 0; i < p.length; i++) {
    const a = p[i];
    const b = p[(i + 1) % p.length];
    s += a.x * b.y - b.x * a.y;
  }
  return Math.abs(s) / 2;
}

function inside(p: Pt, poly: Pt[]): boolean {
  let c = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i];
    const b = poly[j];
    if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) c = !c;
  }
  return c;
}

/** Subdivide a ring into short segments (canonically per edge so neighbours agree) and displace them. */
function roughen(ring: V[], amp: number, step: number): { pts: Pt[]; nb: number[] } {
  const pts: Pt[] = [];
  const nb: number[] = [];
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i];
    const b = ring[(i + 1) % ring.length];
    const flip = a.x > b.x + 1e-7 || (Math.abs(a.x - b.x) <= 1e-7 && a.y > b.y);
    const p = flip ? b : a;
    const q = flip ? a : b;
    const len = Math.hypot(q.x - p.x, q.y - p.y);
    const n = Math.max(1, Math.ceil(Math.round(len * 100) / 100 / step));
    // Build the list of points from a (inclusive) towards b (exclusive).
    const list: Pt[] = [];
    if (!flip) for (let k = 0; k < n; k++) list.push({ x: p.x + ((q.x - p.x) * k) / n, y: p.y + ((q.y - p.y) * k) / n });
    else for (let k = n; k >= 1; k--) list.push({ x: p.x + ((q.x - p.x) * k) / n, y: p.y + ((q.y - p.y) * k) / n });
    for (const pt of list) { pts.push(displace(pt, amp)); nb.push(a.tag); }
  }
  return { pts, nb };
}

export interface LandResult { cells: LandCell[]; bbox: Bounds; radius: number; /** Median nearest-neighbour distance between holdings. */ spacing: number }

/**
 * Voronoi cells (clipped to a per-holding radius) for the given holdings.
 * The radius adapts to local spacing, so sparse holdings become small islands and tight clusters merge into one land.
 */
export function buildLand(holdings: HoldingPoint[]): LandResult {
  const n = holdings.length;
  if (n === 0) return { cells: [], bbox: { x0: 0, y0: 0, x1: 1000, y1: 700 }, radius: 50, spacing: 80 };
  // nearest neighbour spacing
  const nn = new Array<number>(n).fill(Infinity);
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if (i === j) continue;
      const d = Math.hypot(holdings[i].x - holdings[j].x, holdings[i].y - holdings[j].y);
      if (d < nn[i]) nn[i] = d;
    }
    if (!isFinite(nn[i])) nn[i] = 80;
  }
  const sorted = [...nn].sort((a, b) => a - b);
  const med = sorted[Math.floor(sorted.length / 2)];
  const reach = Math.max(34, Math.min(95, med * 1.45));
  const cells: LandCell[] = [];
  const RING = 28;
  for (let i = 0; i < n; i++) {
    const h = holdings[i];
    const R = Math.max(reach * 0.8, Math.min(reach * 1.25, nn[i] * 0.78));
    let poly: V[] = [];
    for (let k = 0; k < RING; k++) {
      const a = (k / RING) * Math.PI * 2;
      const wob = 1 + 0.07 * vnoise(h.x * 0.13 + Math.cos(a) * 2, h.y * 0.13 + Math.sin(a) * 2, 5);
      poly.push({ x: h.x + Math.cos(a) * R * wob, y: h.y + Math.sin(a) * R * wob, tag: -1 });
    }
    for (let j = 0; j < n && poly.length > 2; j++) {
      if (j === i) continue;
      const o = holdings[j];
      const dx = o.x - h.x;
      const dy = o.y - h.y;
      const d = Math.hypot(dx, dy);
      if (d > R * 2.6 || d < 1e-6) continue;
      poly = clipHalf(poly, { x: h.x + dx / 2, y: h.y + dy / 2 }, { x: dx / d, y: dy / d }, j);
    }
    if (poly.length < 3) continue;
    const rough = roughen(poly, 3.4, 9);
    const path = `M${rough.pts.map((p) => `${r1(p.x)} ${r1(p.y)}`).join('L')}Z`;
    cells.push({ idx: i, id: h.id, x: h.x, y: h.y, pts: rough.pts, nb: rough.nb, area: polyArea(rough.pts), path });
  }
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const c of cells) for (const p of c.pts) { x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y); }
  return { cells, bbox: { x0, y0, x1, y1 }, radius: reach, spacing: med };
}

/**
 * Border segments grouped by level. `level(i, j)` gets the holding indices on both sides (j = -1 for coast) and
 * returns a key, or null to skip. Each shared edge is emitted once; consecutive segments merge into polylines.
 */
export function edgePaths(cells: LandCell[], level: (i: number, j: number) => string | null): Map<string, string> {
  const out = new Map<string, string[]>();
  for (const c of cells) {
    const m = c.pts.length;
    const keys: (string | null)[] = c.nb.map((j) => (j !== -1 && j < c.idx ? null : level(c.idx, j)));
    // start the walk at a break so runs never wrap awkwardly
    let start = 0;
    for (let k = 0; k < m; k++) if (keys[k] !== keys[(k + m - 1) % m]) { start = k; break; }
    let run: string | null = null;
    let cur: string | null = null;
    const flush = () => { if (run && cur) { (out.get(cur) ?? out.set(cur, []).get(cur)!).push(run); } run = null; };
    for (let s = 0; s < m; s++) {
      const k = (start + s) % m;
      const key = keys[k];
      if (key !== cur) { flush(); cur = key; }
      if (key === null) continue;
      const a = c.pts[k];
      const b = c.pts[(k + 1) % m];
      if (run === null) run = `M${r1(a.x)} ${r1(a.y)}`;
      run += `L${r1(b.x)} ${r1(b.y)}`;
    }
    flush();
  }
  const res = new Map<string, string>();
  for (const [k, v] of out) res.set(k, v.join(''));
  return res;
}

/**
 * Per-group outline: for every group (e.g. a kingdom) the edges of its cells that face coast or another group.
 * Unlike `edgePaths` each such edge is emitted for the group on its own side, so both neighbours of a shared border get one.
 */
export function groupEdges(cells: LandCell[], groupOf: (i: number) => string): Map<string, string> {
  const out = new Map<string, string[]>();
  for (const c of cells) {
    const g = groupOf(c.idx);
    const m = c.pts.length;
    const on = c.nb.map((j) => j === -1 || groupOf(j) !== g);
    let start = 0;
    for (let k = 0; k < m; k++) if (on[k] !== on[(k + m - 1) % m]) { start = k; break; }
    let run: string | null = null;
    const flush = () => { if (run) { (out.get(g) ?? out.set(g, []).get(g)!).push(run); } run = null; };
    for (let s = 0; s < m; s++) {
      const k = (start + s) % m;
      if (!on[k]) { flush(); continue; }
      const a = c.pts[k];
      const b = c.pts[(k + 1) % m];
      if (run === null) run = `M${r1(a.x)} ${r1(a.y)}`;
      run += `L${r1(b.x)} ${r1(b.y)}`;
    }
    flush();
  }
  const res = new Map<string, string>();
  for (const [k, v] of out) res.set(k, v.join(''));
  return res;
}

const GLYPH_DENSITY: Record<string, { k: string; per: number; size: [number, number] }> = {
  forest: { k: 'tree', per: 1 / 520, size: [9, 13] },
  hills: { k: 'hill', per: 1 / 900, size: [11, 15] },
  mountains: { k: 'peak', per: 1 / 800, size: [12, 17] },
  marsh: { k: 'reed', per: 1 / 800, size: [8, 11] },
  plains: { k: 'tuft', per: 1 / 1500, size: [7, 9] },
  farmland: { k: 'field', per: 1 / 1900, size: [10, 13] },
  coast: { k: 'shell', per: 1 / 1900, size: [7, 9] },
};

/** Sparse decorative glyphs per cell (deterministic). */
export function terrainGlyphs(cells: LandCell[], terrainOf: (c: LandCell) => string): Glyph[] {
  const out: Glyph[] = [];
  for (const c of cells) {
    const spec = GLYPH_DENSITY[terrainOf(c)];
    if (!spec) continue;
    let seed = 0;
    for (let i = 0; i < c.id.length; i++) seed = (seed * 31 + c.id.charCodeAt(i)) >>> 0;
    const rnd = mulberry32(seed || 1);
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
    for (const p of c.pts) { x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y); }
    const want = Math.min(9, Math.max(1, Math.round(c.area * spec.per)));
    const placed: Pt[] = [];
    let tries = 0;
    while (placed.length < want && tries++ < want * 14) {
      const p = { x: x0 + rnd() * (x1 - x0), y: y0 + rnd() * (y1 - y0) };
      if (!inside(p, c.pts)) continue;
      if (Math.hypot(p.x - c.x, p.y - c.y) < 15) continue;
      if (placed.some((q) => Math.hypot(q.x - p.x, q.y - p.y) < 12)) continue;
      // keep clear of the coast / border a little
      const probe = [[5, 0], [-5, 0], [0, 5], [0, -5]].every(([dx, dy]) => inside({ x: p.x + dx, y: p.y + dy }, c.pts));
      if (!probe) continue;
      placed.push(p);
      out.push({ x: r1(p.x), y: r1(p.y), k: spec.k, s: r1(spec.size[0] + rnd() * (spec.size[1] - spec.size[0])), f: rnd() < 0.5 });
    }
  }
  return out;
}

/** Quadratic control point for a road, bowed sideways by a deterministic amount so roads read as hand-laid paths. */
export function roadControl(a: Pt, b: Pt, key: string): Pt {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  const r = mulberry32(h || 1)();
  const mx = (a.x + b.x) / 2;
  const my = (a.y + b.y) / 2;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const bow = (r - 0.5) * 0.34 * len;
  return { x: mx - (dy / len) * bow, y: my + (dx / len) * bow };
}
